import { Select, Text, VStack } from '@genial/components';
import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../hooks/useRecoilQuery';
import { masterStateSelector } from '../../../../../store/master/selector';
import { sessionState } from '../../../../../store/session/atom';

import {
  RequestModalFieldShell,
  requestModalSelectTriggerProps,
  requestModalSelectTriggerTextProps,
} from './requestModalFieldStyles';

export type BranchAdvisorFieldsProps = {
  partnerId?: number;
  advisorId?: number;
  onChangePartner: (partnerId: number) => void;
  onChangeAdvisor: (advisorId: number | undefined) => void;
};

export const BranchAdvisorFields: React.FC<BranchAdvisorFieldsProps> = ({
  partnerId,
  advisorId,
  onChangePartner,
  onChangeAdvisor,
}) => {
  const { userProfile, isMaster } = useRecoilValue(sessionState);
  const canSelectBranchAndAdvisor = !!isMaster;
  const { data: masterData, loading: isMasterLoading } = useRecoilQuery(
    masterStateSelector({ isMaster: !!isMaster })
  );

  const partnerOptions = useMemo(() => {
    if (!canSelectBranchAndAdvisor) {
      return [
        {
          label:
            userProfile?.nomeParceiro || `Filial ${userProfile?.idFilialParceiroIntranet || ''}`,
          value: `${userProfile?.idParceiro || 0}`,
        },
      ];
    }

    return (masterData?.partnerList || [])
      .filter((partner) => partner.id > 0)
      .map((partner) => ({
        label: partner.name,
        value: partner.id.toString(),
      }));
  }, [
    canSelectBranchAndAdvisor,
    masterData?.partnerList,
    userProfile?.idFilialParceiroIntranet,
    userProfile?.idParceiro,
    userProfile?.nomeParceiro,
  ]);

  const advisorOptions = useMemo(() => {
    if (!canSelectBranchAndAdvisor) {
      return [
        {
          label: userProfile?.nomeRecurso || `${userProfile?.idAssessorIntranet || ''}`,
          value: `${userProfile?.idAssessorIntranet || 0}`,
        },
      ];
    }

    return (masterData?.advisorList || [])
      .filter((advisor) => advisor.id > 0 && advisor.partnerId === partnerId)
      .map((advisor) => ({
        label: advisor.name,
        value: advisor.id.toString(),
      }));
  }, [
    canSelectBranchAndAdvisor,
    masterData?.advisorList,
    partnerId,
    userProfile?.idAssessorIntranet,
    userProfile?.nomeRecurso,
  ]);

  return (
    <VStack space="stack.xxxs">
      <VStack space="stack.quarck">
        <Text variant="regular">Filial</Text>
        <RequestModalFieldShell>
          <Select
            testID="report-generator-request-partner-select"
            selectedValue={partnerId && partnerId > 0 ? partnerId.toString() : ''}
            onValueChange={(value) => {
              onChangePartner(Number(value));
              onChangeAdvisor(undefined);
            }}
            options={partnerOptions}
            placeholder="Selecione uma filial"
            disabled={!canSelectBranchAndAdvisor || isMasterLoading}
            maxOptionPerRender={10}
            triggerProps={requestModalSelectTriggerProps}
            triggerTextProps={requestModalSelectTriggerTextProps}
          />
        </RequestModalFieldShell>
      </VStack>
      <VStack space="stack.quarck">
        <Text variant="regular">Assessor</Text>
        <RequestModalFieldShell>
          <Select
            testID="report-generator-request-advisor-select"
            selectedValue={advisorId && advisorId > 0 ? advisorId.toString() : ''}
            onValueChange={(value) => onChangeAdvisor(Number(value))}
            options={advisorOptions}
            placeholder="Selecione um assessor"
            disabled={!canSelectBranchAndAdvisor || isMasterLoading || !partnerId}
            maxOptionPerRender={10}
            triggerProps={requestModalSelectTriggerProps}
            triggerTextProps={requestModalSelectTriggerTextProps}
          />
        </RequestModalFieldShell>
      </VStack>
    </VStack>
  );
};
